/**
 * Color Theme — ClubSwap Brand v1.0
 *
 * Centralized color palette based on the official ClubSwap brand book.
 * Design v2 (Sept 2026): six brand colours, each pulled a step toward grey so the
 * page reads quiet — linen and white, green ink, one accent action.
 *
 * Theme: "Slate (quiet)". The action colour (`Colors.primary` and the accent*
 * tokens) is a slate blue-grey instead of the harvest orange (`Brand.oogstoranje`
 * is kept only for the Harvest theme). The brand mark uses its own steel blue and
 * gold (`Brand.markBlue` / `Brand.markGold`). Greens, linen and the status colours
 * are identical across the Harvest / Ochre / Slate themes.
 */

// ─── Brand palette (named) ────────────────────────────────────────────────────
export const Brand = {
  // Primary
  bosgroen:      '#1E3B31', // Forest green — navbar, headings, prices, base of every shadow
  oogstoranje:   '#D4823A', // Harvest orange — only the Harvest theme's accent; no longer in the logo
  leisteen:      '#4F6D7A', // Slate blue-grey — the action colour: primary button, FAB, active nav, badges
  leisteenMist:  '#A9BFC9', // Slate lifted to saliemist lightness — the accent on Bosgroen
  linnenCreme:   '#F8F3EA', // Stone linen — page ground and secondary panels

  // Brand mark (September 2026): the two figures and arrows on Bosgroen.
  // Logo and app icon only — never used for UI.
  markBlue:      '#7FB3CC', // Steel blue figure, top arrow, "Swap" in the lockup
  markGold:      '#E8B84A', // Gold figure, bottom arrow, tagline

  // Secondary
  klaverengroen: '#3D6B57', // Clover green — secondary text, section icons, captions
  saliemist:     '#B7CDBF', // Sage — text and inactive icons on Bosgroen
  inkt:          '#222222', // Ink — body text
  steengrijs:    '#888888', // Captions & dividers
} as const;

// ─── Semantic color map ───────────────────────────────────────────────────────
export const Colors = {
  // Primary brand colors
  primary:      Brand.leisteen,      // #4F6D7A — fills only: primary button, FAB, unread badge, active nav underline (white on it 5.5:1)
  primaryLight: '#E3E9EC',           // accent-soft — selected rows, icon squares, reserved/fair chips, secondary-button hover
  primaryDark:  '#435D69',           // accent-hover — hover/pressed fill of primary buttons and the FAB
  accentStrong: '#34505B',           // Accent as TEXT (links, view-all, reserved/fair chips, italic first name) — 8.6:1 on white
  accent:       Brand.klaverengroen, // #3D6B57 — secondary accent elements

  // FAB / floating action
  fabAccent:     Brand.leisteen,     // #4F6D7A — FAB primary
  fabAccentDark: '#435D69',          // accent-hover — FAB pressed
  fabGrey:       Brand.saliemist,    // #B7CDBF — FAB secondary variant
  fabGreyDark:   '#9DB5A6',          // Darker sage — FAB secondary pressed

  // Semantic status inks (design v2): dark text/icon colours that sit on the
  // soft tints below and read on white; `error` also fills the danger button.
  success:       '#2E6B44',
  successLight:  '#30D158',          // legacy (ConditionColors)
  warning:       '#7A6220',
  warningYellow: '#FFD60A',          // legacy (ConditionColors)
  error:         '#A63A2F',
  info:          '#355178',          // admin only: buyer-confirmed chips

  // Neutral / backgrounds
  white:               '#FFFFFF',
  background:          Brand.linnenCreme, // #F8F3EA — desktop page ground; secondary panels inside white
  backgroundLight:     '#EFE8DC',         // @deprecated — use Colors.card (sunken) or Colors.primaryLight for selected rows
  contentArea:         '#EFE8DC',         // surface-sunken — admin page ground
  contentAreaDetail:   '#FFFFFF',         // White — detail backgrounds
  border:              '#CDC2AD',         // line-strong — outline buttons, dashed empties, toggle-off, chevrons
  borderLight:         '#E3DCCF',         // line — the hairline: card and input borders, dividers, column edges
  card:                '#EFE8DC',         // surface-sunken — photo placeholders, thumbnail wells
  disabled:            '#CDC2AD',         // Disabled button background

  // Text
  text:        Brand.inkt,          // #222222 — primary body text on light
  textDark:    Brand.bosgroen,      // #1E3B31 — headings, prices, labels on light
  textMedium:  Brand.klaverengroen, // #3D6B57 — secondary text / captions on light
  textLight:   '#6B6B6B',           // muted: meta, timestamps, hints, overlines (5.0:1 on white and linen)

  // Component-specific
  shadow:  Brand.bosgroen,                   // #1E3B31 — shadow base color
  overlay: 'rgba(30, 59, 49, 0.5)',          // Bosgroen overlay for modals
  dangerButton: '#A63A2F',                   // Destructive button fill
  chatSurface:  Brand.linnenCreme,           // Linen behind chat bubbles (design v2)

  // Transparent variants
  primaryTransparent10: 'rgba(79, 109, 122, 0.1)',
  primaryTransparent15: 'rgba(79, 109, 122, 0.15)',
  whiteTransparent10:   'rgba(255, 255, 255, 0.1)',
  whiteTransparent15:   'rgba(255, 255, 255, 0.15)',
  whiteTransparent20:   'rgba(255, 255, 255, 0.2)',
  whiteTransparent70:   'rgba(255, 255, 255, 0.7)',

} as const;

// ─── Condition badge colors ───────────────────────────────────────────────────
export const ConditionColors = {
  new:        Colors.success,
  'like-new': Colors.successLight,
  good:       Colors.warningYellow,
  fair:       Colors.warning,
  default:    Colors.textLight,
} as const;

export const getConditionColor = (condition: string): string => {
  return ConditionColors[condition as keyof typeof ConditionColors] || ConditionColors.default;
};

// ─── Tinted chips (polish round) ─────────────────────────────────────────────
// Condition and status read as soft tinted chips with dark text; the saturated
// system colours above stay reserved for icons, dialog accents and the danger
// button.
export interface Tint {
  bg: string;
  fg: string;
}

export const ConditionTints: Record<string, Tint> = {
  new:        { bg: '#E4EFE6', fg: '#2E6B44' },
  'like-new': { bg: '#E4EFE6', fg: '#2E6B44' },
  good:       { bg: '#F4EED8', fg: '#7A6220' },
  fair:       { bg: '#E3E9EC', fg: '#34505B' },
  default:    { bg: '#EFE8DC', fg: '#3D6B57' },
};

export const getConditionTint = (condition: string): Tint =>
  ConditionTints[condition] || ConditionTints.default;

export const StatusTints = {
  warning: { bg: '#F4EED8', fg: Colors.warning },
  info:    { bg: '#E6EBF2', fg: Colors.info },
  primary: { bg: Colors.primaryLight, fg: Colors.accentStrong },
  success: { bg: '#E4EFE6', fg: Colors.success },
  error:   { bg: '#F5E4E0', fg: Colors.error },
  neutral: { bg: Colors.background, fg: Colors.textMedium },
} as const satisfies Record<string, Tint>;

export type StatusTintKey = keyof typeof StatusTints;

export type ColorKey = keyof typeof Colors;

// ─── Club pastel palette (warm tones to match brand) ─────────────────────────
export const ClubPastelPalette = [
  '#EFDCC8', // Warm peach
  '#EEE2CB', // Soft amber
  '#D5E3D6', // Pale sage
  '#CAD9CF', // Mint mist
  '#EAD7C6', // Blush orange
  '#DFD7C8', // Linen warm
  '#D3E0D3', // Soft clover
  '#E8DCC7', // Pale straw
  '#D8E3D7', // Light saliemist
  '#E9DCD0', // Nude rose
] as const;

export type ClubPaletteIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
